import type { ApplicationType, SectionType } from './types';

/**
 * Fil-felt-hjelpere for søknads-vedlegg.
 *
 * En søknadsmal har to typer fil-opplastingsfelt:
 *   - `fileuploadsingle` → ÉN logisk fil, versjonert (ny opplasting = ny versjon).
 *   - `fileupload`       → FLERE distinkte filer (multiple).
 *
 * `label`/nøkkel på et vedlegg tilsvarer element-`name` i malen. Disse hjelperne
 * lar applicant skille multiple-opplasting fra versjonert enkeltfil.
 */

export type FileField = { name: string; label: string; multiple: boolean };

/** Trekk ut fil-opplastingsfelt fra en mal (sections → forms → elements, inkl.
 *  radiogroupdepending.innerElements). */
export function extractFileFields(
    template: ApplicationType | Record<string, any> | null | undefined,
): FileField[] {
    const sections: SectionType[] =
        (template as any)?.sections ?? (template as any)?.application?.sections ?? [];
    const out: FileField[] = [];

    const walk = (elements: any[] | undefined) => {
        for (const el of elements ?? []) {
            if (el?.type === 'fileupload' || el?.type === 'fileuploadsingle') {
                out.push({
                    name: el.name,
                    label: el.label || el.name,
                    multiple: el.type === 'fileupload',
                });
            }
            if (Array.isArray(el?.depends)) {
                for (const d of el.depends) walk(d.innerElements);
            }
        }
    };

    for (const section of sections) {
        for (const form of ((section as any)?.forms ?? [])) {
            walk(form?.elements);
        }
    }
    return out;
}

/**
 * Radfelt: elementer som lagrer en liste av rader der en kolonne kan være en
 * fil — `expensetable`, eller et element med en kolonne av typen `attachment`.
 *
 * Bilagene deres ligger i `attachments[felt]`, mens radene i `payload[felt]`
 * bare har referansen `{name, location}`. Feltnavnet er altså nøkkel begge
 * steder, og den som slår `attachments` sammen med `payload` må hoppe over
 * disse — ellers erstatter fillista hele tabellen.
 *
 * Speiler `portalApplicationTemplate::rowFileFields()` i lyfti-api.
 */
type RawElement = {
    type?: string;
    name?: string;
    label?: string;
    columns?: { type?: string }[];
    depends?: { innerElements?: RawElement[] }[];
    elements?: RawElement[];
};
type RawTemplate = {
    sections?: { forms?: { elements?: RawElement[] }[] }[];
    application?: RawTemplate;
};

export function extractRowFileFields(
    template: ApplicationType | object | null | undefined,
): { name: string; label: string }[] {
    const raw = (template ?? {}) as RawTemplate;
    const sections = raw.sections ?? raw.application?.sections ?? [];
    const out: { name: string; label: string }[] = [];

    const walk = (elements: RawElement[] | undefined) => {
        for (const el of elements ?? []) {
            const hasAttachmentColumn = Array.isArray(el?.columns)
                && el.columns.some((c) => c?.type === 'attachment');
            if (el?.name && (el.type === 'expensetable' || hasAttachmentColumn)) {
                out.push({ name: el.name, label: el.label || el.name });
            }
            for (const d of Array.isArray(el?.depends) ? el.depends : []) walk(d?.innerElements);
            walk(el?.elements);
        }
    };

    for (const section of sections) {
        for (const form of section?.forms ?? []) {
            walk(form?.elements);
        }
    }
    return out;
}

/**
 * Er verdien en liste av rader (og ikke en liste av filbeskrivelser)?
 * Brukes når malen mangler, for å kjenne igjen et radfelt på dataen.
 * Speiler `isRowList()` i lyfti-api.
 */
export function isRowList(value: unknown): boolean {
    return Array.isArray(value)
        && value.length > 0
        && !looksLikeFileValue(value)
        && value.every((row) => row !== null && typeof row === 'object' && !Array.isArray(row));
}

/** Sett med element-navn som er multiple-opplasting (`fileupload`). */
export function multipleFileFieldNames(
    template: ApplicationType | Record<string, any> | null | undefined,
): Set<string> {
    return new Set(extractFileFields(template).filter((f) => f.multiple).map((f) => f.name));
}

/**
 * Ser verdien ut som en lagret filbeskrivelse (eller en liste av dem)?
 *
 * Signaturen er `name` + `location` — formen API-et legger i
 * `attachments`/`media` og sender tilbake til oss. Speiler
 * `asFileDescriptorList()` i lyfti-api (`applicationReactApi.php`); endrer du
 * den ene, endre den andre.
 *
 * Brukes som sikkerhetsnett i `saveDraft` for felter malen ikke kjenner —
 * typisk et felt som er omdøpt eller fjernet i lyfti-form-builder etter at
 * søkeren lastet opp noe. Uten dette ville beskrivelsene blitt serialisert som
 * `felt[0][name]` inn i `$_POST` og lagret i søknadens payload.
 *
 * Gjelder bare filfelt på toppnivå. `appendToFormData()` bruker den ikke på
 * celler i utleggsrader — der er beskrivelsen raden sin referanse til bilaget.
 */
export function looksLikeFileValue(value: unknown): boolean {
    const isDescriptor = (v: unknown): boolean => {
        if (!v || typeof v !== 'object' || v instanceof File) return false;
        const row = v as Record<string, unknown>;
        return typeof row.name === 'string' && typeof row.location === 'string';
    };

    if (Array.isArray(value)) {
        return value.length > 0 && value.every((v) => v instanceof File || isDescriptor(v));
    }

    return isDescriptor(value);
}

/**
 * Én fil API-et lastet opp i `saveApplicationDraft`/`saveReportDraft`.
 *
 * `path` er stien fila ble sendt under i multipart-kroppen, relativt til
 * feltet: `[0]` for `felt[]`, `[2, 'bilag', 0]` for et bilag i rad 2 av en
 * utleggstabell. `file` er det som ble lagret — for radbilag bare referansen
 * `{name, location}`, som er det raden lagres med.
 */
export type UploadedFile = {
    field: string;
    path: (string | number)[];
    file: Record<string, unknown>;
};

/**
 * Finner igjen hvilket `File`-objekt hver opplastet fil kom fra.
 *
 * `sent` er objektet som ble gitt til `apiCall` — altså formen
 * `appendToFormData()` serialiserte. Stien følges ned til første `File`; den
 * avsluttende indeksen fra `felt[]` treffer da ingenting og ignoreres.
 *
 * @example
 *   sent = { vedlegg: [fileA], tabell: [{ bilag: fileB }] }
 *   uploaded = [{ field: 'tabell', path: [0, 'bilag', 0], file: ref }]
 *   → Map { fileB → ref }
 */
export function mapUploadedFiles(
    sent: Record<string, unknown>,
    uploaded: UploadedFile[] | undefined,
): Map<File, Record<string, unknown>> {
    const out = new Map<File, Record<string, unknown>>();
    for (const u of uploaded ?? []) {
        let cur: unknown = sent[u.field];
        for (const seg of u.path) {
            if (cur instanceof File || cur === null || typeof cur !== 'object') break;
            cur = (cur as Record<string | number, unknown>)[seg];
        }
        if (cur instanceof File) out.set(cur, u.file);
    }
    return out;
}

/**
 * Bytter `File`-objektene i `map` ut med det API-et lagret, hvor enn de ligger
 * i skjemadataene (filfelt, lister, celler i utleggsrader).
 *
 * Uten dette ble lagrede filer liggende som `File` i `formData` og lastet opp
 * på nytt ved hver eneste `saveDraft`. Byttet skjer på objektidentitet, så en
 * fil søkeren la til MENS lagringen pågikk, blir stående og sendes neste gang.
 */
export function replaceUploadedFiles<T>(value: T, map: Map<File, Record<string, unknown>>): T {
    if (map.size === 0) return value;

    // Urørte grener beholder identiteten, så bare det som faktisk inneholdt
    // en byttet fil blir et nytt objekt.
    const walk = (v: unknown): unknown => {
        if (v instanceof File) return map.get(v) ?? v;
        if (Array.isArray(v)) {
            const out = v.map(walk);
            return out.some((x, i) => x !== v[i]) ? out : v;
        }
        if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
            let changed = false;
            const out: Record<string, unknown> = {};
            for (const [k, inner] of Object.entries(v)) {
                out[k] = walk(inner);
                if (out[k] !== inner) changed = true;
            }
            return changed ? out : v;
        }
        return v;
    };

    return walk(value) as T;
}
